import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  catchError,
  debounceTime,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { EMPTY, merge, Subject, throwError } from 'rxjs';
import { SubSink } from 'subsink';
import { PaymentService } from 'src/app/feature/payment/services';
import { PaymentEntity } from 'src/app/shared';
import { PaymentStatus, UserSocket } from 'src/app/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatSort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TitleService } from 'src/app/feature/services';
import { PaymentControlComponent } from '../menu/admin-payments/payment-control/payment-control.component';

import produce from 'immer';

@Component({
  selector: 'admin-user-order-history',
  templateUrl: './admin-user-order-history.component.html',
  styleUrls: ['./admin-user-order-history.component.sass'],
})
export class AdminUserOrderHistoryComponent implements OnInit, AfterViewInit {
  @ViewChild(MatDateRangePicker) dateRangePicker!: MatDateRangePicker<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('idSearch') idSearchInput!: MatInput;
  @ViewChild('type') documentTypes!: MatSelect;
  @ViewChild(MatSort) sort!: MatSort;

  @Input('userId') userId!: number;

  public endDate: string = '';
  public startDate: string = '';
  public PaymentStatus = PaymentStatus;

  range = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });

  public refreshController$ = new Subject();
  public searchController$ = new Subject();

  public isRateLimitReached: boolean = false;
  public isLoadingResult: boolean = false;
  public resultsLength: number = 0;

  public dataSource = new MatTableDataSource<PaymentEntity>();
  public displayedColumns: string[] = [
    'id',
    'status',
    'name',
    'amount',
    'user',
    'actions',
  ];

  // ------------------------------------------------Private methods|

  private subsink = new SubSink();
  private previewDialogRef!: MatDialogRef<any>;

  // ------------------------------------------------Constructor|

  constructor(
    private titleService: TitleService,
    private paymentService: PaymentService,
    private previewDialog: MatDialog,
    private userSocket: UserSocket,
    private cdr: ChangeDetectorRef,
  ) {
    this.titleService.setTitle('تاریخچه سفارشات');
  }

  public getDate(date: string): Date {
    return new Date(date);
  }

  ngOnInit(): void {}
  ngAfterViewInit(): void {
    // The order of below are important
    merge(
      this.paginator.page,
      this.sort.sortChange,
      this.searchController$.pipe(debounceTime(350)),
      this.refreshController$,
      this.documentTypes.valueChange,
      this.userSocket.onConnect$()
    )
      .pipe(
        catchError((err) => {
          this.isLoadingResult = false;
          this.cdr.detectChanges();
          return throwError(err);
        }),
        startWith({}),
        switchMap((value: any, index: any) => {
          this.isLoadingResult = true;
          this.cdr.detectChanges();
          const order = value?.direction
            ? value.direction.toUpperCase()
            : undefined;

          const search = value?.idSearch ? value.idSearch : undefined;

          return this.paymentService
            .loadPaymentForUser(this.userId, {
              limit: this.paginator.pageSize,
              page: this.paginator.pageIndex + 1,
              search: search,
            })
            .pipe(catchError(() => EMPTY));
        }),
        map((data) => {
          this.isLoadingResult = false;
          this.isRateLimitReached = data.items.length === 0;

          if (this.isRateLimitReached) {
            return [];
          }

          this.resultsLength = data.meta.totalItems + 1;
          return data.items;
        })
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(
        (data: any) => (this.dataSource = new MatTableDataSource(data))
      );
  }

  // ---------------------------------------------------------Public methods|

  public preview(payment: PaymentEntity) {
    this.previewDialog
      .open(PaymentControlComponent, {
        data: {
          payment,
        },
        minHeight: '570px',
      })
      .afterClosed()
      .subscribe((anyChange: boolean) => this.refreshController$.next(''));
  }

  public getTotalCost(): number {
    if (this.dataSource.data.length <= 0) return 0;
    return +this.dataSource.data
      .map((item) => item.amount || 0)
      .reduce((a, b) => (+a + +b) as any);
  }

  public exportExcel() {
    const data = this.prepareDataToExport();

    const xlsx = require('xlsx');
    const worksheet = xlsx.utils.json_to_sheet(data);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = xlsx.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    this.saveAsExcelFile(excelBuffer, 'paid_baskets');
  }

  public prepareDataToExport(): any[] {
    return produce(this.dataSource.data, (draft) => {
      draft.forEach((basket: any) => {
        basket.status = this.getStatusString(basket.status!);
        basket.address = `${basket.address?.province} - ${basket.address?.city} - ${basket.address?.address}`;
        basket.transportationMethod = basket.transportationMethod?.name;
        basket.user = basket.user?.id;

        Object.keys(basket).forEach((key) => {
          if (!basket[key]) {
            basket[key] = '0';
          }
        });
      });
    });
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const FileSaver = require('file-saver');

    let EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });

    FileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  public getStatusString(status: PaymentStatus) {
    switch (status) {
      case PaymentStatus.Canceled:
        return 'کنسل شده';
      case PaymentStatus.Done:
        return 'انجام شده';
      case PaymentStatus.Implementation:
        return 'در حال انجام';
      case PaymentStatus.NotDone:
        return 'انجام نشده';

      case PaymentStatus.Payment:
        return 'در انتظار پرداخت';

      case PaymentStatus.Sent:
        return 'ارسال شده';

      default:
        return 'نا مشخص' + `${status}`;
    }
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
